import { Box, CircularProgress, HStack, useToast } from '@chakra-ui/react';
import { Select as ReactSelect } from 'chakra-react-select';
import MediaCard from 'features/common/components/MediaCard/MediaCard';
import { isMarketplaceApp } from 'features/common/config/envConfig';
import {
  isFgaPrimaryDam,
  TenantLabel,
} from 'features/common/utils/tenantLabels';
import {
  GetOrganizationMemberDocument,
  useUpdateOrganizationMemberRolesMutation,
} from 'gql/graphql';
import { useEffect, useState } from 'react';
import getAccessOptions, {
  getSitecoreAiDamAccessLabel,
} from '../utils/getAccessOptions';
import { ITenantSelectOption } from '../utils/memberDetailsModel';

interface MemberDetailsProductAccessRowProps {
  tenant: {
    id: string;
    title: string;
    label: string;
    iconUrl: string;
    productCode: string;
    systemId: string;
    labels?: TenantLabel[] | null;
  };
  selectedValue: ITenantSelectOption;
  isDisabled: boolean;
  userId: string;
  roles: { scope: string; name: string; id: string; description: string }[];
}

const MemberDetailsProductAccessRow = ({
  tenant,
  selectedValue,
  userId,
  isDisabled,
  roles,
}: MemberDetailsProductAccessRowProps) => {
  const toast = useToast();
  const [isUpdating, setIsUpdating] = useState(false);
  // Optimistic selection — cleared once parent confirms or on error.
  const [optimisticValue, setOptimisticValue] =
    useState<ITenantSelectOption | null>(null);
  const { title, id, label, iconUrl, systemId } = tenant;
  const [updateOrganizationMemberRolesMutation] =
    useUpdateOrganizationMemberRolesMutation();
  const noAccessValue = 'no-access';

  // Clear the optimistic value once the parent confirms the same selection.
  useEffect(() => {
    if (optimisticValue && selectedValue.value === optimisticValue.value) {
      setOptimisticValue(null);
    }
  }, [selectedValue, optimisticValue]);

  const productCode = isMarketplaceApp(systemId)
    ? 'Marketplace'
    : tenant.productCode;
  const useHardcodedDamRoles = isFgaPrimaryDam(productCode, tenant.labels);

  const accessOptions = () => [
    ...getAccessOptions(roles, productCode, tenant.labels),
    {
      value: noAccessValue,
      label: 'No access',
    } as ITenantSelectOption,
  ];

  const changeSelectAccess: (accessLevel: string) => void = async (
    currentOption,
  ) => {
    const previousOption = selectedValue.value;

    if (previousOption === currentOption) return;

    setIsUpdating(true);
    setOptimisticValue({
      value: currentOption,
      label:
        (useHardcodedDamRoles
          ? getSitecoreAiDamAccessLabel(currentOption)
          : undefined) ??
        (currentOption === noAccessValue ? 'No access' : currentOption),
    });

    const roleToAdd =
      currentOption === noAccessValue
        ? []
        : [
            {
              productCode: productCode,
              applicationId: id,
              roleName: currentOption,
            },
          ];

    // "No access" is a UI sentinel — no real role to delete from the backend.
    const roleToDelete =
      previousOption === noAccessValue
        ? []
        : [
            {
              productCode: productCode,
              applicationId: id,
              roleName: previousOption,
            },
          ];

    try {
      const { data } = await updateOrganizationMemberRolesMutation({
        variables: {
          applicationRolesToAdd: roleToAdd,
          applicationRolesToDelete: roleToDelete,
          userId: userId,
        },
        refetchQueries: [
          { query: GetOrganizationMemberDocument, variables: { id: userId } },
        ],
      });

      setIsUpdating(false);
      const error = data?.updateOrganizationMemberRoles.errors?.[0];

      if (error) {
        setOptimisticValue(null);
        toast({
          description: error?.message,
          title: 'Error',
          status: 'error',
        });
      }
    } catch (error: any) {
      setIsUpdating(false);
      setOptimisticValue(null);
      const { networkError } = error ?? {};
      toast({
        description: networkError.message,
        title: `Error ${networkError.statusCode}`,
        status: 'error',
      });
    }
  };

  return (
    <HStack w='100%' mb='4' data-testid={id}>
      <Box mr='4' width='sm'>
        <MediaCard
          size='option'
          title={title}
          label={label}
          imageUrl={iconUrl}
        />
      </Box>
      <Box w='44' data-testid='select-tenant-access'>
        <ReactSelect
          aria-label={`${title} access role`}
          selectedOptionColorScheme='primary'
          isDisabled={isUpdating || isDisabled}
          value={optimisticValue ?? selectedValue}
          onChange={(data) => {
            //@ts-ignore will always have value
            changeSelectAccess(data?.value);
          }}
          options={accessOptions()}
        />
      </Box>
      <Box width='36' ml='4' data-testid='test'>
        {isUpdating && (
          <CircularProgress
            data-testid='loading-icon'
            isIndeterminate
            size='5'
          />
        )}
      </Box>
    </HStack>
  );
};

export default MemberDetailsProductAccessRow;
